import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import {
  getProducts,
  getCategories,
  createProduct,
  updateProduct,
  deleteProduct,
  createCategory,
  updateCategory,
  deleteCategory,
  updateProductCategories,
  convertLegacyProduct,
  convertToLegacyProduct,
  clearProductCache,
  clearCategoryCache,
  type Category as SupabaseCategory
} from '../lib/supabase';

export function useSupabaseProducts(section: 'unlimited' | 'robotics' | 'sex-machines') {
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadData = useCallback(async (retryCount = 0) => {
    if (!mountedRef.current) return;
    try {
      if (retryCount === 0) {
        setLoading(true);
        setError(null);
      }

      const [categoriesData, productsData] = await Promise.all([
        getCategories(section),
        getProducts(section, true)
      ]);

      if (!mountedRef.current) return;

      const legacyCategories = categoriesData.map((cat: SupabaseCategory) => ({
        id: cat.id,
        name: cat.name,
        color: cat.color
      }));
      setCategories(legacyCategories);

      const legacyProducts = productsData.map(convertToLegacyProduct);
      setProducts(legacyProducts);

      setError(null);
    } catch (err) {
      console.error('Error loading data from Supabase:', err);
      if (!mountedRef.current) return;

      if (retryCount < 2) {
        setTimeout(() => loadData(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }

      const errorMsg = err instanceof Error ? err.message : 'Neznámá chyba';
      setError(`Chyba načítání: ${errorMsg}`);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [section]);

  useEffect(() => {
    loadData();

    let reloadTimeout: ReturnType<typeof setTimeout> | null = null;

    const channel = supabase
      .channel(`products-changes-${section}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'products',
          filter: `category=eq.${section}`
        },
        () => {
          if (reloadTimeout) clearTimeout(reloadTimeout);
          reloadTimeout = setTimeout(() => loadData(), 500);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'categories',
          filter: `section=eq.${section}`
        },
        () => {
          if (reloadTimeout) clearTimeout(reloadTimeout);
          reloadTimeout = setTimeout(() => loadData(), 500);
        }
      )
      .subscribe();

    return () => {
      if (reloadTimeout) clearTimeout(reloadTimeout);
      try {
        supabase.removeChannel(channel);
      } catch (err) {
        console.error('Error removing channel:', err);
      }
    };
  }, [section, loadData]);

  const addProduct = async (productData: any) => {
    try {
      const maxOrderIndex = products.length > 0
        ? Math.max(...products.map(p => p.order || 0))
        : 0;

      const supabaseProductData = convertLegacyProduct({
        ...productData,
        category: section,
        order: maxOrderIndex + 1
      });

      const newProduct = await createProduct(supabaseProductData);

      if (productData.categories && productData.categories.length > 0) {
        await updateProductCategories(newProduct.id, productData.categories);
      }

      clearProductCache();
      await loadData();

      return convertToLegacyProduct(newProduct);
    } catch (err) {
      console.error('Error adding product:', err);
      throw err;
    }
  };

  const editProduct = async (productData: any) => {
    try {
      const supabaseProductData = convertLegacyProduct(productData);

      const [updatedProduct] = await Promise.all([
        updateProduct(productData.id, supabaseProductData),
        productData.categories ? updateProductCategories(productData.id, productData.categories) : Promise.resolve()
      ]);

      clearProductCache();

      return convertToLegacyProduct(updatedProduct);
    } catch (err) {
      console.error('Error updating product:', err);
      throw err;
    }
  };

  const removeProduct = async (id: string) => {
    try {
      await deleteProduct(id);
      clearProductCache();
      await loadData();
    } catch (err) {
      console.error('Error deleting product:', err);
      throw err;
    }
  };

  const addCategory = async (categoryData: { name: string; color: string }) => {
    try {
      const newCategory = await createCategory({
        ...categoryData,
        section: section as 'unlimited' | 'robotics' | 'sex-machines'
      });

      clearCategoryCache();
      await loadData();
      return newCategory;
    } catch (err) {
      console.error('Error adding category:', err);
      throw err;
    }
  };

  const editCategory = async (id: string, categoryData: { name: string; color: string }) => {
    try {
      const updatedCategory = await updateCategory(id, categoryData);
      clearCategoryCache();
      await loadData();

      return updatedCategory;
    } catch (err) {
      console.error('Error updating category:', err);
      throw err;
    }
  };

  const removeCategory = async (id: string) => {
    try {
      await deleteCategory(id);
      clearCategoryCache();
      await loadData();
    } catch (err) {
      console.error('Error deleting category:', err);
      throw err;
    }
  };

  return {
    products,
    categories,
    loading,
    error,
    addProduct,
    editProduct,
    removeProduct,
    addCategory,
    editCategory,
    removeCategory,
    reload: () => loadData()
  };
}
